import { Card, Descriptions, Table, Tag, Button, List, Space, Typography, Divider, Flex, Col, Badge, Row, Popover } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { format, formatDistanceToNow } from "date-fns";
import { Show, TextField } from "@refinedev/antd";
import { useShow } from "@refinedev/core";
import { useTable } from "@refinedev/antd";
import { IconDisplay } from "../../../components/IconDisplay";
import TextArea from "antd/es/input/TextArea";

const formatDate = (timestamp: number | null) => {
	if (!timestamp) return "N/A";
	return dayjs(Math.floor(timestamp / 1e6)).format("MM/dd/yy hh:mm a");
};

const { Title, Text } = Typography;

export const SubscriptionShow = () => {
  const { id } = useParams(); // Assuming you use a routing system like React Router
  const navigate = useNavigate();
  const { query } = useShow({
    resource: "subscription",
    id,
  });

	const { data, isLoading } = query;
    const subscription: any = data?.data.data;

    const { tableProps } = useTable({
        resource: `subscription/invoice/${id}`,
        syncWithLocation: true,
        queryOptions: {
            keepPreviousData: false,
            cacheTime: 20,
        },
    })

    const invoiceColumns = [
        {
            title: 'Created at',
            dataIndex: 'createdAt',
            render: (value: any) => (
                <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                  <span>{formatDistanceToNow(value)} ago</span>
                </Popover>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            render: (amount: any) => (
                <TextField value={`$ ${amount}`} />
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
        },
        {
            title: 'Paid at',
            dataIndex: 'paidAt',
            render: (value: any) => (
                value ? (
                    <Popover content={format(value, "MM/dd/yy hh:mm a")}>
                        <span>{formatDistanceToNow(value)} ago</span>
                    </Popover>
                ) : (
                    <TextField value="N/A" />
                )
            )
        }
    ]

    // console.log("Subscription", subscription)
    return (
        <Show isLoading={isLoading}>
          <Card style={{ padding: 5 }}>
            <Title level={4}>Subscription details</Title>
            {/* User details */}
            <Row gutter={60} align="top" justify="start" style={{ marginLeft: 20}} >

              {/* Created at */}
              <Col style={{ marginTop: 10}}>
                  <Title level={5}>Created at</Title>
                  <div
                  >
                    {!isLoading && (
                      <Popover content={formatDate(subscription?.createdAt || null)}>
                        <span>
                            {formatDistanceToNow(format(subscription?.createdAt, "MM/dd/yy hh:mm a"))} ago
                        </span>
                      </Popover>
                    )}
                    
                  </div>
              </Col>

              {/* Status */}
              <Col style={{ marginTop: 10 }}>
                <Title level={5}>Status</Title>                  
                  <Tag
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {subscription?.status}
                  </Tag>
              </Col>
                  
              {/* Start date */}
              <Col style={{ marginTop: 10}}>
                  <Title level={5}>Start date</Title>
                  <div
                  >
                    {!isLoading && (
                      <Popover content={formatDate(subscription?.startDate || null)}>
                        <span>
                            {formatDistanceToNow(format(subscription?.startDate, "MM/dd/yy hh:mm a"))} ago
                        </span>
                      </Popover>
                    )}
                    
                  </div>
              </Col>

              <Col style={{ marginTop: 10}}>
                  <Title level={5}>End date</Title>
                  <div
                  >
                    {!isLoading && (
                      <Popover content={formatDate(subscription?.endDate || null)}>
                        <span>
                            {formatDistanceToNow(format(subscription?.endDate, "MM/dd/yy hh:mm a"))} ago
                        </span>
                      </Popover>
                    )}
                    
                  </div>
              </Col>
            </Row>


            {/* Plan details */}
            <Divider />

            <Title level={4}>Plan details</Title>
            <Row gutter={50} align="top" justify="start">
              {/* Plan name */}
              <Col style={{ marginBottom: 20, marginLeft: 50 }}>
                <Title level={5}>Name</Title>
                <TextField value={subscription?.product.name} />
              </Col>

              {/* Plan description */}
              <Col style={{ marginBottom: 20, marginLeft: 50  }}>
                <Title level={5}>Description</Title>
                {subscription?.product.description.length > 0 ? (
                    <TextField value={subscription?.product.description} />

                ) : (
                    <TextField value="N/A" />
                )}
              </Col>

              {/* Product status */}
              <Col style={{ marginBottom: 20, marginLeft: 50  }}>
                <Title level={5}>Plan status</Title>
                <Tag>{subscription?.product.status}</Tag>
              </Col>

              {/* Product price*/}
              <Col style={{ marginBottom: 20, marginLeft: 50  }}>
                <Title level={5}>Plan price</Title>
                <TextField value={subscription?.product.monthlyPrice} />
              </Col>
            </Row>
    
            <Divider />

            {/* Subscriber details */}
            <Title level={4}>Subscriber details</Title>
            <Col style={{ marginLeft: 50 }}>
                {/* Row for Email */}
                <Row gutter={[10, 10]} align="middle" style={{ marginBottom: "16px" }}>
                  <Col span={6}>
                    <Title level={5} style={{ margin: 0 }}>Email</Title>
                  </Col>
                  <Col span={6}>
                    <TextField
                      value={subscription?.user?.email}
                      onClick={() => {
                        navigate(`/users/${subscription?.user?.id}`);
                      }}
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                      }}
                    />
                  </Col>
                </Row>

                {/* Row for Name */}
                <Row gutter={[10, 10]} align="middle">
                  <Col span={6}>
                    <Title level={5} style={{ margin: 0 }}>Name</Title>
                  </Col>
                  <Col span={6}>
                  <TextField
                    value={subscription?.user?.profile?.fullName}
                    onClick={() => {
                      navigate(`/users/${subscription?.user?.id}`);
                    }}
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                    }}
                  />
                  </Col>
                </Row>
            </Col>

            <Divider />

            <Title level={4} style={{  }}>Invoices</Title>
            <Row gutter={50} align="top" justify="start">
                <List
                    style={{
                        margin: 50,
                        width: '70%'
                    }}
                >
                  <Table
                    {...tableProps}
                    columns={invoiceColumns}
                    rowKey="id"
                    pagination={{
                      ...tableProps.pagination,
                      showTotal: (total) => `Total ${total} items`,
                      showSizeChanger: true,
                      showQuickJumper: true,
                    }}
                  />
                </List>
            </Row>

          </Card>
        </Show>
      );
};
